import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
    Typography,
    Box,
    Card,
    CardContent,
    Stack,
    CircularProgress,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Link
} from '@mui/material';
import { styled } from '@mui/material/styles';
// ArrowBackIcon removed — using text "←" for back link to match HTML version
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import config from '../config';
//import Cookies from 'js-cookie';
import { createQuote } from '../utils/quoter/quoteCreator';
import logger from '../utils/logging/logger';
import AuthDialog from '../components/auth/AuthDialog';
import { loginPromptManager, LoginPromptType } from '../utils/auth/loginPromptManager';
//import { authService } from '../utils/auth/authService';
import { useAuth } from '../hooks/auth/useAuth';
import sessionManager from '../utils/session/sessionManager';

const componentName = 'SailSelection';

// Styling constants
const CARD_WIDTH = '320px';  // Matching HTML version tile size
const CARD_HEIGHT = '300px';  // Matching HTML version min-height
const GRID_MAX_WIDTH = '1000px';
const SAIL_IMAGE_HEIGHT = '140px';  // Smaller to match HTML version

// Styled component for sail card
const SailCard = styled(Card)(() => ({
    width: CARD_WIDTH,
    border: '2px solid #e9ecef',
    borderColor: '#e9ecef',
    minHeight: CARD_HEIGHT,
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
        borderColor: '#FF6B35',
        boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
        transform: 'translateY(-4px)',
    },
    '&:hover .configure-btn': {
        backgroundColor: '#E44D26',
    },
}));

// Always-visible orange CTA button on sail cards
const ConfigureButton = styled(Box)(() => ({
    backgroundColor: '#FF6B35',
    color: '#ffffff',
    padding: '12px 24px',
    borderRadius: '0 0 4px 4px',
    fontWeight: 600,
    fontSize: '0.95rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'background-color 0.2s ease',
}));

// Trust badge container
const TrustBadgeContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '24px',
    marginBottom: '16px',
    flexWrap: 'wrap',
    [theme.breakpoints.down('md')]: {
        gap: '12px',
    },
}));

// Trust badge item with optional highlight prop
const TrustBadgeItem = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'highlight',
})<{ highlight?: boolean }>(({ theme, highlight }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#1a3a5c',
    fontSize: '0.9rem',
    fontWeight: 500,
    ...(highlight && {
        background: 'linear-gradient(135deg, #1a3a5c 0%, #2D5F8A 100%)',
        color: 'white',
        padding: '6px 14px',
        borderRadius: '20px',
        fontWeight: 600,
    }),
    '& svg': {
        color: '#FF6B35',
        fontSize: '1.25rem',
        flexShrink: 0,
    },
    [theme.breakpoints.down('md')]: {
        fontSize: '0.8rem',
        ...(highlight && {
            padding: '5px 12px',
        }),
    },
}));

// Value prop hero banner
const ValuePropHero = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    margin: '20px 0',
    padding: '18px 20px',
    background: 'linear-gradient(135deg, #1a3a5c 0%, #2D5F8A 100%)',
    borderRadius: '12px',
    color: 'white',
    [theme.breakpoints.down('md')]: {
        padding: '20px 16px',
        margin: '16px 0',
    },
}));

type Boat = {
    BoatId: number;
    BoatModel: string;
    Boat_UrlId?: string;  // SEO-friendly URL identifier
    I: number | null;
    J: number | null;
    P: number | null;
    E: number | null;
    BoatLengthFt: number;
    Boat_ImgName: string | null;
};

//s_SailSelectList - Direct query to vBoat_Sail
type Sail = {
    SailId: number;
    SailName: string;
    SailDescription: string;
    SailAreaSqFt: number | null;
    SailAreaMeters: number | null;
    Sail_ImgName: string;
    SailCutId: number;
    PercentLP: number;
    SailCutName: string;
    IsFurling: number;
    ClothId: number;
    ClothDisplayName: string;
    Manufacturer: string | null;
    SailTypeId: number;
    TotalDisplayPrice: number;
};

const formatMeasurement = (value: number | null | undefined): string => {
    if (typeof value === 'number') {
        return value.toFixed(2);
    }
    return 'N/A';
};

const SailSelection: React.FC = () => {
    const { boatIdentifier } = useParams<{ boatIdentifier: string }>();
    const boatId = boatIdentifier; // Can be either numeric ID or SEO-friendly slug
    const navigate = useNavigate();
    const [boat, setBoat] = useState<Boat | null>(null);
    const [sails, setSails] = useState<Sail[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    // Add state for tracking quote creation
    const [isCreatingQuote, setIsCreatingQuote] = useState(false);
    const [quoteError, setQuoteError] = useState<string | null>(null);

    // Authentication state
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);

    // Get auth information from our hooks
    const { user, isInitializing, isAnonymous, isAuthenticated, refreshUserInfo } = useAuth();


    // Learn More modal state
    const [showLearnMoreModal, setShowLearnMoreModal] = useState(false);

    //More user prompt --------------------------------------------------------------------------------------
    const [showAccountPrompt, setShowAccountPrompt] = useState(false);
    const [showAuthDialog, setShowAuthDialog] = useState(false);
    const [authPromptType, setAuthPromptType] = useState<LoginPromptType>(LoginPromptType.CONVERT_ANONYMOUS);

    // Check for showLogin query parameter (from SEO pages)
    const [searchParams, setSearchParams] = useSearchParams();

    // Open auth dialog if showLogin=true in URL (e.g., from SEO page login link)
    useEffect(() => {
        if (searchParams.get('showLogin') === 'true' && !isInitializing) {
            setAuthPromptType(LoginPromptType.SIGN_IN);
            setShowAuthDialog(true);
            // Remove the query param so dialog doesn't reopen on refresh
            searchParams.delete('showLogin');
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams, isInitializing]);

    const handleLoginClick = () => {
        setShowAccountPrompt(false);
        setAuthPromptType(LoginPromptType.SIGN_IN);
        setShowAuthDialog(true);
    };

    const handleCreateAccount = () => {
        setShowAccountPrompt(false);
        setAuthPromptType(LoginPromptType.CREATE_ACCOUNT);
        setShowAuthDialog(true);
    };

    // Add these handler functions
    const handleAccountPromptClose = () => {
        setShowAccountPrompt(false);
    };

    const handleAuthDialogClose = () => {
        setShowAuthDialog(false);
    };

    const handleAuthSuccess = () => {
        // You could refresh user info or perform other actions after successful login
        logger.log('LogVerbose', componentName, 'User logged in successfully');

        // Refresh user info to update the header display
        refreshUserInfo();

        // Force a page reload after successful login
        //window.location.reload();
    };

    // PHASE 1: Auth prompt removed - now shown at checkout instead
    // Authentication prompt will be shown in CartDrawer before submission
    // This allows users to freely browse and configure without interruption


    //End user prompt -------------------------------------------------------

    // Fetch boat and sail data
    useEffect(() => {
        const fetchBoatAndSailData = async () => {
            if (!boatId) {
                setError('No boat specified');
                setLoading(false);
                return;
            }

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            abortControllerRef.current = new AbortController();

            try {
                setLoading(true);
                setError(null);

                const requestOptions = {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    signal: abortControllerRef.current.signal
                };

                const [boatResponse, sailsResponse] = await Promise.all([
                    fetch(`${config.API_URL}/api/boat/${boatId}`, requestOptions),
                    fetch(`${config.API_URL}/api/sails/${boatId}`, requestOptions)
                ]);

                // Check if responses are successful
                if (!boatResponse.ok) {
                    throw new Error(`Failed to fetch boat data: ${boatResponse.status} ${boatResponse.statusText}`);
                }
                if (!sailsResponse.ok) {
                    throw new Error(`Failed to fetch sails data: ${sailsResponse.status} ${sailsResponse.statusText}`);
                }

                const [boatData, sailsData] = await Promise.all([
                    boatResponse.json(),
                    sailsResponse.json()
                ]);

                // Ensure sailsData is an array
                if (!Array.isArray(sailsData)) {
                    console.error('Sails data is not an array:', sailsData);
                    setSails([]);
                } else {
                    setSails(sailsData);
                }
                setBoat(boatData);
            } catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                    return; // Ignore aborted requests
                }
                console.error('Error fetching data:', error);
                setError(error instanceof Error ? error.message : 'An unknown error occurred');
            } finally {
                setLoading(false);
            }
        };

        fetchBoatAndSailData();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [boatId]);

    // Handle user selecting a sail
    const handleSailSelect = async (sail: Sail, isFurling: number = 0) => {
        if (sail.SailAreaSqFt === null) return;

        // If price is $0, open mailto link instead of creating a quote
        if (!sail.TotalDisplayPrice) {
            const subject = encodeURIComponent(`Price Request: ${sail.SailName} for ${boat?.BoatModel || 'My Boat'}`);
            const body = encodeURIComponent(
                `Hi Point Sails,\n\n` +
                `I would like to request pricing for the following sail:\n\n` +
                `Boat: ${boat?.BoatModel || 'Unknown'}\n` +
                `Sail: ${sail.SailName}\n` +
                `Sail Cut: ${sail.SailCutName}\n` +
                `Cloth: ${sail.ClothDisplayName}\n` +
                (sail.IsFurling ? `Furling: Yes\n` : '') +
                `\nPlease contact me with pricing information.\n\n` +
                `Thank you!`
            );
            window.location.href = `mailto:crew@pointsails.com?subject=${subject}&body=${body}`;
            return;
        }

        if (!boat || !boat.BoatId) {
            console.error('Boat data is missing');
            setQuoteError("Missing boat information. Please try again.");
            setIsCreatingQuote(false);
            return;
        }

        setIsCreatingQuote(true);
        setQuoteError(null);

        //** Save selection to cookie
        const userId = user.userId;
        const numericBoatId = boat.BoatId; // Use the numeric BoatId from the boat object

        // We should add a SAIL_SELECTION_KEY_FORMAT to config
        const stateKey = config.SAIL_SELECTION_KEY_FORMAT
            .replace('{userId}', userId.toString())
            .replace('{groupId}', '0')
            .replace('{boatId}', numericBoatId.toString())
            .replace('{sailId}', sail.SailId.toString());

        // Store using sessionManager with proper config values
        sessionManager.storeData(
            stateKey,
            {
                selectedSailArea: sail.SailAreaSqFt,
                selectedSailAreaMeters: sail.SailAreaMeters,
                selectedSailCutId: sail.SailCutId || 0,
                isFurling: isFurling,
                timestamp: Date.now()
            },
            config.COOKIE_EXPIRY_DAYS || 7
        );

        const sailCutId = sail.SailCutId || 0;

        try {
            // Create the quote using centralized auth via quoteCreator
            // Use numeric BoatId from boat object, not the URL parameter which might be a slug
            const newQuoteId = await createQuote(
                numericBoatId,
                sail.SailId,
                sail.SailAreaSqFt.toString(),
                sailCutId.toString(),
                isFurling,
                user.userId
            );

            if (newQuoteId > 0) {
                // Navigate to the sail options page with the new quote ID
                // Use numeric BoatId for the URL (sail options page expects numeric ID)
                logger.log('LogVerbose', componentName, 'Navigating to sail options with quote ID:', newQuoteId);
                navigate(`/quoter/sail-options-select/${numericBoatId}/${sail.SailId}/${sail.SailAreaSqFt}/${newQuoteId}/${sailCutId}/${isFurling ? 1 : 0}`);
            } else {
                setQuoteError("Failed to create quote. Support has been notified. Please contact us via email or try again later.");
            }
        } catch (error) {
            console.error('Error creating quote:', error);
            setQuoteError("Failed to create quote. Please try again.");
        } finally {
            setIsCreatingQuote(false);
        }
    };

    // Handle login dialog close
    const handleLoginClose = () => {
        setShowLoginPrompt(false);
    };

    // Handle successful login
    const handleLoginSuccess = () => {
        // You could refresh user info or perform other actions after successful login
        logger.log('LogVerbose', componentName, 'User logged in successfully');
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!boat) {
        return (
            <Typography sx={{ textAlign: 'center', mt: 4 }}>
                Boat information not available
            </Typography>
        );
    }

    //More user prompt --------------------------------------------------------------------------------------
    interface AccountPromptDialogProps {
        open: boolean;
        onClose: () => void;
        onCreateAccount: () => void;
        onLogin: () => void;
    }

    const AccountPromptDialog: React.FC<AccountPromptDialogProps> = (props) => {
        const { open, onClose, onCreateAccount, onLogin } = props;

        logger.log('LogVerbose', componentName, 'AccountPromptDialog render with props', {
            open,
            hasOnClose: !!onClose,
            hasOnCreateAccount: !!onCreateAccount,
            hasOnLogin: !!onLogin
        });

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                aria-labelledby="account-prompt-dialog-title"
            >
                <DialogTitle id="account-prompt-dialog-title">Save Your Quotes</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Would you like to create an account in order to save your quotes for future reference?
                        This will be needed when placing an order. The only required personal information
                        is an email address.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', gap: 2 }}>
                        <Button
                            onClick={onLogin}
                            color="primary"
                            variant="outlined"
                        >
                            Login
                        </Button>
                        <Button
                            onClick={onCreateAccount}
                            color="primary"
                            variant="contained"
                        >
                            Create Account
                        </Button>
                    </Box>
                    <Button
                        onClick={onClose}
                        color="primary"
                        variant="outlined"
                        fullWidth
                    >
                        Continue as Guest
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    //End user prompt --------------------------------------------------------------------------------------


    return (
        <Box sx={{ p: 3, maxWidth: GRID_MAX_WIDTH, margin: '0 auto' }}>

            {/* Account Prompt Dialog */}
            <AccountPromptDialog
                open={showAccountPrompt}
                onClose={handleAccountPromptClose}
                onCreateAccount={handleCreateAccount}
                onLogin={handleLoginClick}
            />

            {/* Auth Dialog - only shown after user clicks "Create Account" or "Login" */}
            <AuthDialog
                open={showAuthDialog}
                onClose={handleAuthDialogClose}
                defaultPromptType={authPromptType}
                onSuccess={handleAuthSuccess}
            />

            {/* Loading overlay */}
            {isCreatingQuote && (
                <Box
                    position="fixed"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor="rgba(255,255,255,0.7)"
                    zIndex={9999}
                >
                    <CircularProgress />
                    <Typography ml={2}>Creating quote...</Typography>
                </Box>
            )}

            {/* Error dialog */}
            {quoteError && (
                <Box
                    position="fixed"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor="rgba(255,255,255,0.9)"
                    zIndex={9999}
                >
                    <Box bgcolor="error.light" p={3} borderRadius={2} maxWidth="80%">
                        <Typography color="error.contrastText" variant="h6" gutterBottom>Error</Typography>
                        <Typography color="error.contrastText">{quoteError}</Typography>
                        <Button
                            variant="contained"
                            sx={{ mt: 2, bgcolor: "error.dark", color: "white" }}
                            onClick={() => setQuoteError(null)}
                        >
                            Dismiss
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Title Row: Back link + Boat Name inline (matching HTML layout) */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    mb: 2,
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 1, md: 0 },
                }}
            >
                <RouterLink
                    to="/quoter"
                    style={{
                        textDecoration: 'none',
                        color: '#4a90a4',
                        fontSize: '13px',
                        fontWeight: 500,
                        display: 'inline-flex',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            position: { xs: 'static', md: 'absolute' },
                            left: 0,
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        ← Select a Different Boat
                    </Box>
                </RouterLink>
                <Typography variant="h4" align="center" sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#1a3a5c' }}>
                    {boat.BoatModel}
                </Typography>
            </Box>

            {/* Trust Badges */}
            <TrustBadgeContainer>
                <TrustBadgeItem highlight>
                    <CheckCircleIcon />
                    30+ Years Experience
                </TrustBadgeItem>
                <TrustBadgeItem>
                    <LocationOnIcon />
                    Designed in San Diego
                </TrustBadgeItem>
                <TrustBadgeItem>
                    <WorkspacePremiumIcon />
                    Built at a world-class loft
                </TrustBadgeItem>
                <RouterLink to="/warranty" style={{ textDecoration: 'none' }}>
                    <TrustBadgeItem sx={{ '&:hover': { textDecoration: 'underline' } }}>
                        <BookmarkIcon />
                        2-Year Warranty
                    </TrustBadgeItem>
                </RouterLink>
            </TrustBadgeContainer>

            {/* Combined Boat Image and Specifications Container - matching SEO template.html */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 3,
                    alignItems: 'flex-start',
                    my: 3,
                    border: '3px solid #e9ecef',
                    borderRadius: '10px',
                    padding: '25px',
                    backgroundColor: '#ffffff',  // White background to stand out from gray page
                    flexDirection: { xs: 'column', md: 'row' },  // Stack on mobile, side-by-side on desktop
                }}
            >
                {/* Boat Image */}
                {boat.Boat_ImgName && (
                    <Box
                        sx={{
                            flex: { xs: '1', md: '0 0 45%' },
                            textAlign: 'center',
                            background: 'transparent',
                        }}
                    >
                        <Box
                            component="img"
                            src={`/images/boats/${boat.Boat_ImgName}`}
                            alt={boat.BoatModel}
                            width={300}
                            height={510}
                            sx={{
                                maxHeight: '250px',
                                width: '100%',
                                objectFit: 'contain',
                                borderRadius: '8px',
                            }}
                            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                const target = e.currentTarget;
                                target.style.display = 'none';
                            }}
                        />
                    </Box>
                )}

                {/* Boat Specifications */}
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 0,
                        background: 'transparent',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h6" sx={{ fontSize: '1.2rem', color: '#2D5F8A', mb: 1.5, fontWeight: 600 }}>
                        Boat Specifications
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1rem', color: '#2c5f73', fontWeight: 500, lineHeight: 1.8 }}>
                        {boat.I !== null && boat.J !== null && (
                            <>
                                <Box component="span" sx={{ fontWeight: 600, color: '#1a1a1a', display: 'inline-block', minWidth: '60px' }}>I:</Box> {formatMeasurement(boat.I)}
                                <br />
                                <Box component="span" sx={{ fontWeight: 600, color: '#1a1a1a', display: 'inline-block', minWidth: '60px' }}>J:</Box> {formatMeasurement(boat.J)}
                                <br />
                            </>
                        )}
                        <Box component="span" sx={{ fontWeight: 600, color: '#1a1a1a', display: 'inline-block', minWidth: '60px' }}>P:</Box> {formatMeasurement(boat.P)}
                        <br />
                        <Box component="span" sx={{ fontWeight: 600, color: '#1a1a1a', display: 'inline-block', minWidth: '60px' }}>E:</Box> {formatMeasurement(boat.E)}
                        {boat.BoatLengthFt && (
                            <>
                                <br />
                                <Box component="span" sx={{ fontWeight: 600, color: '#1a1a1a', display: 'inline-block', minWidth: '60px' }}>Length:</Box> {boat.BoatLengthFt} ft
                            </>
                        )}
                    </Typography>
                </Box>
            </Box>

            {/* Value Prop Hero Banner */}
            <ValuePropHero>
                <Typography
                    sx={{
                        fontSize: { xs: '0.9rem', md: '1.1rem' },
                        fontWeight: 600,
                        color: '#FF6B35',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        display: 'inline-block',
                        mb: 1.5,
                    }}
                >
                    No waiting for quotes. Configure your sail and watch the price update in real time.
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        fontSize: '0.9rem',
                        color: 'rgba(255, 255, 255, 0.9)',
                    }}
                >
                    <span>Premium Challenge Sailcloth</span>
                    <Box component="span" sx={{ opacity: 0.5 }}>&#x2022;</Box>
                    <span>5-7 weeks delivery</span>
                    <Box component="span" sx={{ opacity: 0.5 }}>&#x2022;</Box>
                    <span>No-hassle expert review before payment</span>
                </Box>

                {/* "New to Point Sails?" integrated into hero */}
                <Box
                    sx={{
                        mt: 2,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        cursor: 'pointer',
                        padding: '10px 20px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        border: '1px solid rgba(255, 255, 255, 0.4)',
                        borderRadius: '6px',
                        color: '#ffffff',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            background: 'rgba(255, 255, 255, 0.3)',
                            borderColor: 'rgba(255, 255, 255, 0.6)',
                        },
                    }}
                    onClick={() => setShowLearnMoreModal(true)}
                >
                    <InfoOutlinedIcon sx={{ fontSize: '1.1rem', color: '#FF6B35' }} />
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#ffffff' }}>
                        New to Point Sails? See how we work
                    </Typography>
                </Box>
            </ValuePropHero>

            {/* Learn More Modal */}
            <Dialog
                open={showLearnMoreModal}
                onClose={() => setShowLearnMoreModal(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        color: '#1a3a5c',
                        fontWeight: 600,
                        fontSize: '1.5rem',
                        pb: 1,
                    }}
                >
                    What to Expect When You Order
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Box component="span" sx={{ mr: 1.5, color: '#2D5F8A' }}>•</Box>
                            Every sail designed by Chip – over 30 years of experience in San Diego
                        </Typography>
                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Box component="span" sx={{ mr: 1.5, color: '#2D5F8A' }}>•</Box>
                            Built at the same world-class facility used by major global sail brands
                        </Typography>
                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Box component="span" sx={{ mr: 1.5, color: '#2D5F8A' }}>•</Box>
                            Clear pricing with no surprises – what you see is what you pay
                        </Typography>
                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Box component="span" sx={{ mr: 1.5, color: '#2D5F8A' }}>•</Box>
                            Most sails completed and shipped in 5-7 weeks
                        </Typography>
                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Box component="span" sx={{ mr: 1.5, color: '#2D5F8A' }}>•</Box>
                            Direct communication with real sailmakers, not a call center
                        </Typography>
                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Box component="span" sx={{ mr: 1.5, color: '#2D5F8A' }}>•</Box>
                            2-year warranty on design, manufacturing, and materials
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => setShowLearnMoreModal(false)}
                        sx={{
                            backgroundColor: '#1a3a5c',
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: '#0d2440',
                            },
                            px: 3,
                            py: 1,
                        }}
                    >
                        Got it, let's choose a sail
                    </Button>
                </DialogActions>
            </Dialog>

            <Typography variant="h4" align="center" gutterBottom sx={{ mb: 1, mt: 4, fontSize: '2.125rem' }}>
                Select Your Sail
            </Typography>
            <Typography
                align="center"
                sx={{
                    fontSize: '0.8125rem',
                    color: '#666',
                    mb: 3
                }}
            >
                Click any sail to configure options and see detailed pricing.
            </Typography>

            {/* Group sails by category */}
            {(() => {
                const mainsails = sails.filter(s => s.SailId === 1);
                const headsails = sails.filter(s => s.SailId === 3);
                const downwindSails = sails.filter(s => s.SailId !== 1 && s.SailId !== 3);

                const renderSailGroup = (groupSails: Sail[], title: string) => {
                    if (groupSails.length === 0) return null;
                    return (
                        <Box key={title} sx={{ mb: 4 }}>
                            <Typography
                                variant="h5"
                                align="center"
                                sx={{
                                    mb: 2,
                                    fontWeight: 600,
                                    color: '#1a3a5c',
                                    fontSize: '1.5rem'
                                }}
                            >
                                {title}
                            </Typography>
                            <Stack
                                direction="row"
                                flexWrap="wrap"
                                spacing={2}
                                justifyContent="center"
                                sx={{ mx: 0 }}
                            >
                                {groupSails.map((sail, index) => {
                                    const reactKey = `${sail.SailId}-${sail.SailCutId}-${sail.IsFurling}-${index}`;
                                    return (
                                        <Box key={reactKey} sx={{ p: 2, width: { xs: '100%', md: 'auto' } }}>
                                            <SailCard
                                                onClick={() => handleSailSelect(sail, sail.IsFurling)}
                                            >
                                                <CardContent sx={{
                                                    flex: 1,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    p: 3,
                                                    pb: 2,
                                                }}>
                                                    <Box
                                                        component="img"
                                                        src={`/images/SailDrawings/${sail.Sail_ImgName}`}
                                                        alt={sail.SailName}
                                                        sx={{
                                                            width: '100%',
                                                            height: SAIL_IMAGE_HEIGHT,
                                                            objectFit: 'contain',
                                                            mb: 2
                                                        }}
                                                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                                            const target = e.currentTarget;
                                                            target.style.display = 'none';
                                                        }}
                                                    />
                                                    <Typography variant="subtitle1" align="center" sx={{ fontWeight: 600, mb: 1.5, fontSize: '1.25rem', color: '#1a1a1a' }}>
                                                        {[3].includes(sail.SailId) ? `${sail?.PercentLP || 130}% ${sail.SailName}` : `${sail.SailName}`}
                                                    </Typography>
                                                    <Stack spacing={0} sx={{ mt: 0.5 }}>
                                                        <Typography variant="body2" align="center" sx={{ color: '#666', fontSize: '0.95rem' }}>
                                                            {sail.Manufacturer ? `Premium ${sail.Manufacturer} Sailcloth` : 'Premium Sailcloth'}
                                                        </Typography>
                                                        <Typography variant="body2" align="center" sx={{ fontWeight: 'bold', fontSize: '1.5rem', color: '#2D5F8A', mt: 2 }}>
                                                            {sail.TotalDisplayPrice ? `$${sail.TotalDisplayPrice.toLocaleString()}` : 'Contact Us For Price'}
                                                        </Typography>
                                                    </Stack>
                                                </CardContent>
                                                <ConfigureButton className="configure-btn">
                                                    Configure Your Sail
                                                    <ArrowForwardIcon sx={{ fontSize: '1rem' }} />
                                                </ConfigureButton>
                                            </SailCard>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </Box>
                    );
                };

                return (
                    <>
                        {renderSailGroup(mainsails, 'Mainsails')}
                        {renderSailGroup(headsails, 'Headsails')}
                        {renderSailGroup(downwindSails, 'Downwind Sails')}
                    </>
                );
            })()}

            {/* Contact Section */}
            <Box sx={{
                mt: 2,
                mb: 3,
                p: 2.5,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                textAlign: 'center'
            }}>
                <Typography variant="body1" color="text.secondary">
                    Have questions?{' '}
                    <Link
                        href="mailto:crew@pointsails.com"
                        sx={{
                            color: '#FF6B35',
                            fontWeight: 600,
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                    >
                        Email
                    </Link>
                    {' '}or call us at{' '}
                    <Link
                        href="tel:+13609037268"
                        sx={{
                            color: '#FF6B35',
                            fontWeight: 600,
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                    >
                        (360) 903-7268
                    </Link>
                    {' '}/{' '}
                    <Link
                        href="tel:+16196639330"
                        sx={{
                            color: '#FF6B35',
                            fontWeight: 600,
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                    >
                        (619) 663-9330
                    </Link>
                    {' '}&mdash; we're here to help!
                </Typography>
            </Box>
        </Box>
    );
};

export default SailSelection;
